import React, { useState, useCallback } from 'react';
import {
  Modal,
  Steps,
  Upload,
  Button,
  Table,
  Alert,
  Tag,
  Result,
  Space,
  Typography,
  Tooltip,
  Progress,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import Papa from 'papaparse';
import { useResponsive } from '../../hooks/useResponsive';
import { getApiBaseUrl } from '../../config/api';
import type { BulkImportResult, ColumnConfig, CsvPreviewRow } from '../../types/import';

const { Text, Title } = Typography;
const { Dragger } = Upload;

interface CsvImportModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  templateEndpoint: string;
  templateFileName: string;
  expectedColumns: ColumnConfig[];
  onImport: (formData: FormData) => Promise<BulkImportResult>;
  onImportSuccess?: () => void;
}

const CsvImportModal: React.FC<CsvImportModalProps> = ({
  open,
  onClose,
  title,
  templateEndpoint,
  templateFileName,
  expectedColumns,
  onImport,
  onImportSuccess,
}) => {
  const { isMobile } = useResponsive();
  const [currentStep, setCurrentStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<CsvPreviewRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null);

  const reset = useCallback(() => {
    setCurrentStep(0);
    setFile(null);
    setPreviewRows([]);
    setImporting(false);
    setImportResult(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleDownloadTemplate = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    const url = `${getApiBaseUrl()}${templateEndpoint}`;

    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = templateFileName;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  }, [templateEndpoint, templateFileName]);

  const validateRow = useCallback(
    (data: Record<string, string>): string[] => {
      const errors: string[] = [];
      for (const col of expectedColumns) {
        const value = data[col.name] ?? '';
        if (col.required && !value.trim()) {
          errors.push(`"${col.label}" es requerido`);
        }
        if (value.trim() && col.type === 'number' && isNaN(Number(value))) {
          errors.push(`"${col.label}" debe ser un número`);
        }
        if (value.trim() && col.validate) {
          const error = col.validate(value);
          if (error) errors.push(error);
        }
      }
      return errors;
    },
    [expectedColumns]
  );

  const handleFileSelected = useCallback(
    (selectedFile: File) => {
      setFile(selectedFile);

      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: true,
        encoding: 'UTF-8',
        complete: (results) => {
          const rows: CsvPreviewRow[] = results.data.map(
            (row: any, index: number) => {
              const data: Record<string, string> = {};
              for (const col of expectedColumns) {
                data[col.name] = row[col.name] ?? '';
              }
              const errors = validateRow(data);
              return {
                rowNumber: index + 2,
                data,
                errors,
                isValid: errors.length === 0,
              };
            }
          );
          setPreviewRows(rows);
          setCurrentStep(1);
        },
        error: () => {
          setPreviewRows([]);
          setCurrentStep(1);
        },
      });
    },
    [expectedColumns, validateRow]
  );

  const handleImport = useCallback(async () => {
    if (!file) return;

    setImporting(true);
    setCurrentStep(2);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await onImport(formData);
      setImportResult(result);
      if (result.successCount > 0) {
        onImportSuccess?.();
      }
    } catch (err: any) {
      setImportResult({
        totalRows: previewRows.length,
        successCount: 0,
        errorCount: 1,
        categoriesCreated: 0,
        errors: [
          {
            rowNumber: 0,
            rowData: {},
            errors: [err?.data?.message || err?.message || 'Error desconocido al importar'],
          },
        ],
      });
    } finally {
      setImporting(false);
    }
  }, [file, onImport, onImportSuccess, previewRows.length]);

  const validCount = previewRows.filter((r) => r.isValid).length;
  const invalidCount = previewRows.filter((r) => !r.isValid).length;

  const previewColumns = [
    {
      title: '#',
      dataIndex: 'rowNumber',
      key: 'rowNumber',
      width: 50,
    },
    ...expectedColumns
      .filter((col) => col.required || !isMobile)
      .slice(0, isMobile ? 3 : undefined)
      .map((col) => ({
        title: col.label,
        dataIndex: ['data', col.name],
        key: col.name,
        ellipsis: true,
        render: (_: any, record: CsvPreviewRow) => record.data[col.name] || '-',
      })),
    {
      title: 'Estado',
      key: 'status',
      width: 120,
      render: (_: any, record: CsvPreviewRow) =>
        record.isValid ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Válido
          </Tag>
        ) : (
          <Tooltip title={record.errors.join(', ')}>
            <Tag color="error" icon={<CloseCircleOutlined />}>
              {record.errors.length} error{record.errors.length > 1 ? 'es' : ''}
            </Tag>
          </Tooltip>
        ),
    },
  ];

  const errorColumns = [
    { title: 'Fila', dataIndex: 'rowNumber', key: 'rowNumber', width: 60 },
    {
      title: 'Datos',
      key: 'rowData',
      render: (_: any, record: any) =>
        Object.entries(record.rowData || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join(' | ') || '-',
      ellipsis: true,
    },
    {
      title: 'Errores',
      dataIndex: 'errors',
      key: 'errors',
      render: (errors: string[]) => (
        <Space direction="vertical" size={0}>
          {errors.map((e, i) => (
            <Text key={i} type="danger" style={{ fontSize: 12 }}>
              {e}
            </Text>
          ))}
        </Space>
      ),
    },
  ];

  const renderStep0 = () => (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          message="Instrucciones"
          description={
            <ol style={{ textAlign: 'left', margin: 0, paddingLeft: 20 }}>
              <li>Descarga la plantilla CSV</li>
              <li>Llena los datos en la plantilla</li>
              <li>Sube el archivo CSV completado</li>
            </ol>
          }
          type="info"
          showIcon
        />

        <Button
          icon={<DownloadOutlined />}
          onClick={handleDownloadTemplate}
          type="default"
          size="large"
        >
          Descargar Plantilla CSV
        </Button>

        <Dragger
          accept=".csv"
          maxCount={1}
          showUploadList={false}
          beforeUpload={(f) => {
            handleFileSelected(f);
            return false;
          }}
          style={{ padding: '20px 0' }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Haz clic o arrastra un archivo CSV aquí
          </p>
          <p className="ant-upload-hint">Solo archivos .csv</p>
        </Dragger>
      </Space>
    </div>
  );

  const renderStep1 = () => (
    <div>
      <Space
        direction="vertical"
        size="middle"
        style={{ width: '100%', marginBottom: 16 }}
      >
        {previewRows.length === 0 ? (
          <Alert
            message="No se encontraron filas de datos en el archivo CSV"
            type="error"
            showIcon
          />
        ) : (
          <>
            <Space>
              <Tag color="success">
                {validCount} válida{validCount !== 1 ? 's' : ''}
              </Tag>
              {invalidCount > 0 && (
                <Tag color="error">
                  {invalidCount} con errores
                </Tag>
              )}
              <Text type="secondary">{previewRows.length} filas total</Text>
            </Space>

            <Table
              dataSource={previewRows}
              columns={previewColumns}
              rowKey="rowNumber"
              size="small"
              pagination={previewRows.length > 10 ? { pageSize: 10, size: 'small' } : false}
              scroll={{ x: 'max-content' }}
              rowClassName={(record) => (record.isValid ? '' : 'ant-table-row-error')}
            />
          </>
        )}
      </Space>

      <Space>
        <Button icon={<ArrowLeftOutlined />} onClick={() => { setCurrentStep(0); setFile(null); setPreviewRows([]); }}>
          Atrás
        </Button>
        {validCount > 0 && (
          <Button type="primary" icon={<UploadOutlined />} onClick={handleImport}>
            Importar {validCount} fila{validCount !== 1 ? 's' : ''}
          </Button>
        )}
      </Space>
    </div>
  );

  const renderStep2 = () => (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      {importing ? (
        <Space direction="vertical" size="large">
          <Progress type="circle" status="active" percent={99} />
          <Text>Importando datos...</Text>
        </Space>
      ) : importResult ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Result
            status={importResult.successCount > 0 ? 'success' : 'error'}
            title={
              importResult.successCount > 0
                ? `${importResult.successCount} registros importados`
                : 'Error en la importación'
            }
            subTitle={
              <Space direction="vertical">
                {importResult.categoriesCreated > 0 && (
                  <Text>
                    {importResult.categoriesCreated} categoría{importResult.categoriesCreated !== 1 ? 's' : ''} creada{importResult.categoriesCreated !== 1 ? 's' : ''}
                  </Text>
                )}
                {importResult.errorCount > 0 && (
                  <Text type="danger">
                    {importResult.errorCount} fila{importResult.errorCount !== 1 ? 's' : ''} con errores
                  </Text>
                )}
              </Space>
            }
          />

          {importResult.errors.length > 0 && (
            <div style={{ textAlign: 'left' }}>
              <Title level={5}>Errores:</Title>
              <Table
                dataSource={importResult.errors}
                columns={errorColumns}
                rowKey="rowNumber"
                size="small"
                pagination={importResult.errors.length > 5 ? { pageSize: 5, size: 'small' } : false}
                scroll={{ x: 'max-content' }}
              />
            </div>
          )}
        </Space>
      ) : null}
    </div>
  );

  return (
    <Modal
      open={open}
      title={title}
      onCancel={handleClose}
      width={isMobile ? '100%' : 800}
      footer={
        currentStep === 2 && !importing
          ? [
              <Button key="close" type="primary" onClick={handleClose}>
                Cerrar
              </Button>,
            ]
          : null
      }
      destroyOnClose
    >
      <Steps
        current={currentStep}
        size="small"
        style={{ marginBottom: 24 }}
        items={[
          { title: isMobile ? 'Subir' : 'Subir archivo' },
          { title: 'Preview' },
          { title: isMobile ? 'Resultado' : 'Resultados' },
        ]}
      />

      {currentStep === 0 && renderStep0()}
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
    </Modal>
  );
};

export default CsvImportModal;
